import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Axios from 'axios';
import LoadingModal from '../../Layout/LoadingModal.js';
import { FaCheck, FaMinus, FaCloudDownloadAlt, FaTrash } from 'react-icons/fa';
import { IoCloseOutline } from 'react-icons/io5';
import { VscOpenPreview } from 'react-icons/vsc';
import { useAlert } from 'react-alert';
import { confirmAlert } from 'react-confirm-alert';
import FileDownload from 'js-file-download';
import ReactSelect from 'react-select';
import Button from 'react-bootstrap/Button';
import dayjs from 'dayjs';

const rowTitles = ['Fecha', 'Válido por (días)', '¿Cumple?', 'Ver archivo generado', 'Descargar', 'Eliminar'];

let CancelToken = Axios.CancelToken;
let source = CancelToken.source();

const IntVerFixedScalesExported = ({ openPreviewModal }) => {

  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [availableScales, setAvailableScales] = useState([]);
  const [selectedScale, setSelectedScale] = useState('');
  const [data, setData] = useState([]);

  const availableScalesSelectOpts = useMemo(() => { 
    let optsHelper = [{ value: '', label: 'Vacío' }];
    availableScales.forEach(item => {
      optsHelper.push({ value: item.code, label: item.code })
    })
    return optsHelper;
  }, [availableScales])

  const isFirstRender = useRef(true);

  const GetAvailableScales = useCallback(() => {

    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = { cancelToken: source.token };
    const url = '/api/extras/intVerFixedScales';

    Axios.get(url, opts)
    .then((res) => {
      setLoading(false);
      if (res.data?.data?.length) {
        setAvailableScales(res.data.data);
      }
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    });
  }, [alert]);

  const GetData = useCallback(() => {

    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = { cancelToken: source.token };
    const url = '/api/intVer/fixedScales/exported/'+selectedScale;

    Axios.get(url, opts)
    .then((res) => {
      setLoading(false);
      if (res.data?.length) {
        const sortedData = res.data.sort((a, b) => (b.date.localeCompare(a.date, 'es', {numeric: true})));
        setData(sortedData);
      }
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    });
  }, [alert, selectedScale]);

  const DeleteRecordStepOne = (idx) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <div className="modal-delete-confirm-container">
            <h4>Está eliminando una verificación intermedia</h4>
            <p> {'¿Está seguro(a) de eliminar la verificación '}{<b>{data[idx].code + ' - ' + dayjs(data[idx].date.split('T')[0]).tz('America/Santiago').format('DD/MM/YYYY')}</b>}{'?'} </p>
            <div className="d-flex mt-3">
              <Button className="flex-1 silver-black me-2" onClick={() => onClose()}> No, deseo salir </Button>
              <Button className="flex-1 lb-white" onClick={() => { onClose(); DeleteRecordStepTwo(idx) }}> Si, deseo eliminar </Button>
            </div>
          </div>
        );
      }
    })
  }

  const DeleteRecordStepTwo = (idx) => {
    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = { cancelToken: source.token };
    const url = '/api/intVer/fixedScales/delete';

    Axios.post(url, { code: data[idx].code, date: data[idx].date }, opts)
    .then((res) => {
      setLoading(false);
      alert.show(res.data.msg, {type: 'success', timeout: 10000});
      GetData();
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else {
        alert.show(err.response.data.msg, {type: 'error'})
      }
    });
  }

  const DownloadReportFile = (code, date) => {
    
    setLoading(true);

    if (source) {
      source.cancel();
      source = CancelToken.source();
    }

    const opts = {
      headers: {
        'Content-Disposition': 'attachment'
      },
      responseType: 'blob',
      cancelToken: source.token
    };

    const url = '/api/downloads/intVer/fixedScale/single/'+code+'&'+date;

    Axios.get(url, opts)
    .then((res) => {      
      const downloadTitle = code + ' - ' + dayjs(date.split('T')[0]).format('DD-MM-YYYY') + '.pdf';
      FileDownload(res.data, downloadTitle);
      setLoading(false);
      alert.show('Se ha descargado su verificación con éxito', {type: 'success'})
    })
    .catch((err) => {
      setLoading(false);
      if (Axios.isCancel(err)) {
        alert.show('Petición cancelada', {type: 'info'});
      } else if (!err.response) {
        console.log('Petición cancelada');
      } else if (err.response.status === 500) {
        alert.show('No se recibió respuesta del servidor', {type: 'error'})
      } else if (err.response.status === 404) {
        alert.show('No se pudo descargar el archivo', {type: 'error'})
      } else if (err.response.status === 400) {
        alert.show('No se encontró el archivo', {type: 'error'})
      } else {
        alert.show('Ocurrió un error al realizar esta operación', {type: 'error'})
      }
    });
  }

  useEffect(() => {
    if (isFirstRender.current) {
      GetAvailableScales();
    }
  }, [GetAvailableScales]);

  useEffect(() => {
    if (selectedScale) {
      GetData();
    }
  }, [selectedScale, GetData]);

  useEffect(() => { isFirstRender.current = false }, []);

  return (
    <>
      <LoadingModal loading={loading}/>
      <div className="mb-2">
        <ReactSelect 
          placeholder={'Seleccione una Balanza'} 
          noOptionsMessage={({ inputValue: string }) => 'No hay resultados'} 
          defaultValue={selectedScale} 
          onChange={(e) => setSelectedScale(e.value)} 
          className="modal-form-input"
          options={availableScalesSelectOpts} 
        />
      </div>
      <div className="modal-table-outer-container">
        <div className="modal-table-row-inner-container">
          <div className="modal-table-sticky-header-container">
            <div className="modal-table-sticky-header">
              { rowTitles.map((item, itemIdx) => <div key={itemIdx} className="modal-table-row-container"> {item} </div> )}
            </div>
          </div>
          {data.length ?
            data.map((item, itemIdx) => {
              const testStatusBgColor = item.testStatus === 'true' ? 'bg-success' : item.testStatus === 'false' ? 'bg-danger' : 'bg-secondary';
              const testStatusTitle = item.testStatus === 'true' ? 'Cumple' : item.testStatus === 'false' ? 'No cumple' : 'Faltan datos';
              return (
                <div key={itemIdx} className="modal-table-row-input-container">
                  <div className="modal-table-row-container">
                    <span className="modal-table-title"> {item.date ? dayjs(item.date.split('T')[0]).tz('America/Santiago').format('DD/MM/YYYY') : '-'} </span>
                  </div>
                  <div className="modal-table-row-container">
                    <span className="modal-table-title"> {item.validDays || '-'} </span>
                  </div>
                  <div className="modal-table-row-container">
                    <Button className={"d-flex p-1 rounded-circle " + testStatusBgColor } title={testStatusTitle}>
                      {item.testStatus === 'true' ? <FaCheck color="white" className="icon-style"/> :
                        item.testStatus === 'false' ? <IoCloseOutline color="white" className="icon-style"/> : <FaMinus color="white" className="icon-style"/> }
                    </Button>
                  </div>
                  <div className="modal-table-row-container">
                    <Button 
                      className="d-flex p-1 rounded-circle bg-lb-white" 
                      disabled={loading} 
                      title="Ver archivo generado"
                      onClick={() => openPreviewModal([{ code: item.code, date: item.date }], 2 )}
                    >
                      <VscOpenPreview color="white" className="icon-style"/>
                    </Button>
                  </div>
                  <div className="modal-table-row-container">
                    <Button 
                      className="d-flex p-1 rounded-circle bg-lb-white" 
                      disabled={loading} 
                      title="Descargar archivo generado"
                      onClick={() => DownloadReportFile(item.code, item.date)}
                    >
                      <FaCloudDownloadAlt color="white" className="icon-style"/>
                    </Button>
                  </div>
                  <div className="modal-table-row-container">
                    <Button className="d-flex p-1 rounded-circle bg-lb-white" disabled={loading} title="Eliminar" onClick={() => DeleteRecordStepOne(itemIdx)}>
                      <FaTrash color="white" className="icon-style"/>
                    </Button>
                  </div>
                </div>
              )
            })
          :
            <span className="modal-table-loading-text"> No hay información para mostrar </span>
          }
        </div>
      </div>
    </>
  )
}

export default IntVerFixedScalesExported;